'use client'

import { FlowchartComponent } from '@/components/Flowchart/Flowchart'
import { FlowchartContext } from '@/components/Flowchart/FlowchartContext'
import { useScrollbarVisibility } from '@/hooks/useScrollbarVisibility'
import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { useGetWorkflowQuery } from '@/lib/features/workflow/workflowApi'
import { Fit, Layout, useRive } from '@rive-app/react-webgl2'
import React, { useCallback, useEffect, useState } from 'react'
import { Node, ReactFlowProvider } from 'reactflow'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'

interface FlowchartViewerProps {
  workflowId?: string | null
  selectedNodeId?: string // Optional node to focus on
  setSelectedNodes?: (updater: React.SetStateAction<string[]>) => void // Function to update selected nodes
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
  workflowConfig?: WorkflowConfig
  nodeExecutions?: Record<string, NodeExecution>
  workflowExecutionId?: string
}

// Container component with scrollbar handling
function FlowchartViewerContainer({ children }: { children: React.ReactNode }) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Handle scrollbar visibility on scroll
  useScrollbarVisibility(containerRef)

  return (
    <div
      ref={containerRef}
      className="scrollbar-auto relative h-full w-full overflow-y-hidden bg-white/20"
    >
      <div className="h-full w-full">{children}</div>
    </div>
  )
}

export default function FlowchartViewer({
  workflowId,
  selectedNodeId,
  setSelectedNodes,
  onOpenPanel,
  workflowConfig,
  nodeExecutions,
  workflowExecutionId,
}: FlowchartViewerProps) {
  const { updateSelectedNodes, context, workflowState, status } = useCopilotContext()

  const {
    data: workflowData,
    isLoading: isWorkflowLoading,
    error: workflowError,
  } = useGetWorkflowQuery(workflowId as string, {
    skip: !workflowId || workflowState.isBuilding,
  })

  // Get values from context
  const selectedNodeIds = context.selectedNodeIds || []

  // Rive animation for creating workflow
  const { RiveComponent: ThinkingAnimation } = useRive({
    src: '/animation/final-light.riv',
    stateMachines: 'State Machine 1',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
    }),
  })

  // Config should be right? But I can't get the animation to trigger properly so leaving this here
  // for the next commit to fix this
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { RiveComponent: DoneAnimation } = useRive({
    src: '/animation/final-done-animation-light.riv',
    stateMachines: 'State Machine 1',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
    }),
  })

  // Keep track of last building workflow data
  const [lastBuildingWorkflow, setLastBuildingWorkflow] = useState<any>(null)

  // Update last building workflow when building state changes
  useEffect(() => {
    if (
      (!workflowData?.data || workflowState.isBuilding) &&
      context.buildingWorkflow?.partialWorkflow
    ) {
      setLastBuildingWorkflow(context.buildingWorkflow.partialWorkflow)
    }
  }, [lastBuildingWorkflow, workflowState.isBuilding, context.buildingWorkflow?.partialWorkflow])

  // Prioritize building workflow data when actively building,
  // keep last building data until query completes, otherwise use RTK Query data
  const flowchartData: WorkflowConfig | undefined =
    workflowConfig ||
    (workflowState.isBuilding && context.buildingWorkflow?.partialWorkflow
      ? context.buildingWorkflow.partialWorkflow
      : lastBuildingWorkflow || workflowData?.data)

  // Set selected node when selectedNodeId changes
  useEffect(() => {
    if (selectedNodeId) {
      updateSelectedNodes([selectedNodeId])
    }
  }, [selectedNodeId, updateSelectedNodes])

  // Handle node click
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!flowchartData) {
        return
      }

      // Prevent selection of visual start/end nodes
      if (node.id === '__start__' || node.id === '__end__') return

      const flowNode = flowchartData.nodes.find((n: any) => n.id === node.id)
      if (!flowNode) return

      // Check if ctrl/cmd key is held for multi-selection
      const isMultiSelect = event.ctrlKey || event.metaKey
      const isSelected = selectedNodeIds.includes(flowNode.id)

      if (isMultiSelect) {
        // Ctrl+click: toggle individual node selection without affecting others
        if (isSelected) {
          updateSelectedNodes(selectedNodeIds.filter((id: string) => id !== flowNode.id))
        } else {
          updateSelectedNodes([...selectedNodeIds, flowNode.id])
        }
      } else {
        // Regular click behavior
        if (isSelected) {
          // Node is already selected
          if (selectedNodeIds.length === 1) {
            // If it's the only selected node, unselect it
            updateSelectedNodes([])
          } else {
            // If multiple nodes are selected, unselect all others (keep only this one)
            updateSelectedNodes([flowNode.id])
          }
        } else {
          // Node is not selected, select only this node
          updateSelectedNodes([flowNode.id])
        }
      }
    },
    [flowchartData, selectedNodeIds, updateSelectedNodes]
  )

  // Handle background click to deselect all nodes
  const onPaneClick = useCallback(() => {
    updateSelectedNodes([])
  }, [updateSelectedNodes])

  // Create a setSelectedNodes-compatible function for context
  const setSelectedNodesCompat = useCallback(
    (updater: React.SetStateAction<string[]>) => {
      if (typeof updater === 'function') {
        updateSelectedNodes(updater(selectedNodeIds))
      } else {
        updateSelectedNodes(updater)
      }
    },
    [selectedNodeIds, updateSelectedNodes]
  )

  // Show loading state for existing workflows (not during creation)
  if (isWorkflowLoading || (workflowId && !flowchartData && !workflowState.isBuilding)) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <div className="text-sm text-gray-500">Loading workflow...</div>
        </div>
      </div>
    )
  }

  // Show error state
  if (workflowError && !workflowState.isBuilding) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <div className="mb-2 text-red-600">Error loading workflow</div>
          <div className="text-sm text-gray-500">
            {('data' in workflowError
              ? 'message' in (workflowError.data as { message: string })
                ? (workflowError.data as { message: string }).message
                : JSON.stringify(workflowError.data, null, 2)
              : JSON.stringify(workflowError, null, 2)) || 'Unknown error'}
          </div>
        </div>
      </div>
    )
  }

  // Show empty state when no workflow is selected or during creation
  if (!flowchartData) {
    // If we're building a workflow, show building state instead of empty state
    if (workflowState.isBuilding) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-64 w-64">
              <ThinkingAnimation />
            </div>
            <div className="text-sm text-gray-500">Creating workflow...</div>
            <div className="mt-1 text-xs text-gray-400">
              Nodes will appear here as they&apos;re created
            </div>
          </div>
        </div>
      )
    }

    // Default empty state
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <div className="mb-2 text-gray-500">No workflow selected</div>
          <div className="text-sm text-gray-400">Create a workflow in the chat to view it here</div>
        </div>
      </div>
    )
  }

  // Show flowchart
  return (
    <div className="relative h-full w-full">
      <FlowchartContext.Provider
        value={{
          selectedNodes: selectedNodeIds,
          setSelectedNodes: setSelectedNodes || setSelectedNodesCompat,
        }}
      >
        <FlowchartViewerContainer>
          <ReactFlowProvider>
            <FlowchartComponent
              flowchartData={flowchartData}
              selectedNodes={selectedNodeIds}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onOpenPanel={onOpenPanel}
              isWorkflowExecuting={status === 'streaming'}
              workflowVersion={workflowData?.metadata?.currentVersion}
              nodeExecutions={nodeExecutions}
              workflowExecutionId={workflowExecutionId}
            />
          </ReactFlowProvider>
        </FlowchartViewerContainer>
      </FlowchartContext.Provider>
    </div>
  )
}
